/**
 * Сущность «Чертёж» — типы предметной области.
 *
 * Чертёж состоит из СЛОЁВ. Каждый слой — набор типизированных объектов одного
 * рода плюс флаг видимости. Движки (2D-холст, 3D-сцена, развёртки) читают слои
 * из стора проекта и только рисуют их: сами они ничего не хранят.
 */

import { LayerKind } from "./layerKind";
import { CatalogSectionKind } from "./catalogSectionKey";
import type { Pt } from "@/shared/lib/geometry";
import type { WallMaterial } from "@/shared/catalog";
import type {
	FloorLayout,
	FloorMaterial,
	WaterproofMaterial,
	ScreedMaterial,
	CeilingMaterial,
	BaseboardMaterial,
	WaterMedium,
} from "@/shared/catalog";

export interface PlanNode {
	id: string;
	x: number;
	y: number;
}

export interface Wall {
	id: string;
	a: string;
	b: string;
	thickness: number;
	height: number;
	material?: WallMaterial;
	bearing?: boolean;
	/** Стена исходного плана, помеченная под снос на этапе «Демонтаж» */
	demolished?: boolean;
	/** Новая стена (перегородка), возводимая на этапе «Монтаж» */
	mounted?: boolean;
}

export type OpeningKind = "door" | "window";

export interface Opening {
	id: string;
	wallId: string;
	kind: OpeningKind;
	offset: number;
	width: number;
	height: number;
	sill: number;
	/**
	 * Тип проёма — id элемента раздела «Двери» или «Окна» (по `kind`): обычная или раздвижная
	 * дверь, окно из двух или трёх створок. Нет (старые проекты) — первый элемент раздела.
	 */
	item?: string;
	flipHinge?: boolean;
	flipSwing?: boolean;
}

export interface CatalogRef {
	catalog: CatalogSectionKind;
	item: string;
}

export interface Fixture {
	id: string;
	wallId: string;
	ref: CatalogRef;
	offset: number;
	elevation: number;
	side: 1 | -1;
	/** Личные габариты перекрывают типовые размеры элемента каталога. */
	width?: number;
	height?: number;
	depth?: number;
	supplyPhases?: 1 | 3;
	lightingControl?: "connections";
}

/**
 * Объект, стоящий свободно на плоскости, — центр в мм и поворот. На полу это мебель
 * (слой своего раздела), на потолке — светильники (в `items` слоя электрики):
 * какая плоскость, решает `attach` типа в каталоге, движки поднимают потолочный
 * объект под потолок комнаты сами.
 */
export interface FloorItem {
	id: string;
	ref: CatalogRef;
	x: number;
	y: number;
	rotation: number;
	autoWiring?: boolean;
	/** Отражён слева направо (до поворота): угловой диван с углом на другую сторону */
	mirrored?: boolean;
	/** Отметка низа от чистого пола; без неё берётся типовая из каталога. */
	elevation?: number;
	/** Цвет, выбранный пользователем, — только у моделей, которые можно перекрасить (`tintable`) */
	color?: string;
	/**
	 * Обивка — ткань или кожа из библиотеки текстур (`fabricTextureById`), только у мягкой
	 * мебели (`upholstery` у модели). Нет — модель в своей обивке.
	 */
	fabric?: string;
	/** Личные габариты перекрывают типовые размеры элемента каталога. */
	width?: number;
	height?: number;
	depth?: number;
}

export interface RoomFloor {
	id: string;
	roomId: string;
	material: FloorMaterial;
	layout?: FloorLayout;
	length?: number;
	width?: number;
	gap?: number;
	tileColor?: string;
	seamColor?: string;
	raise?: number;
	rotation?: number;
	/** Смещение начала раскладки относительно комнаты, мм. */
	offsetX?: number;
	offsetY?: number;
	/** id текстуры пола из библиотеки (`floorTextureById`); нет — покрытие одним цветом */
	texture?: string;
}

export interface RoomWaterproof {
	id: string;
	roomId: string;
	material: WaterproofMaterial;
}

export interface RoomScreed {
	id: string;
	roomId: string;
	material: ScreedMaterial;
}

/**
 * Подвесной потолок комнаты: вид из раздела «Потолок» и то, что пользователь
 * поменял поверх каталога. Нет поля — берётся типовое (`ceilingLook`).
 */
export interface RoomCeiling {
	id: string;
	roomId: string;
	material: CeilingMaterial;
	/** Опуск от перекрытия, мм */
	drop?: number;
	color?: string;
}

/** Плинтус комнаты: вид из каталога и пользовательские изменения его высоты и цвета. */
export interface RoomBaseboard {
	id: string;
	roomId: string;
	material: BaseboardMaterial;
	height?: number;
	color?: string;
}

/**
 * Кабель по потолку между двумя приборами слоя электрики. Концы — id прибора
 * на стене (`fixtures`) или светильника на потолке (`items`); их положение не
 * хранится, а считается из плана. `points` — повороты трассы.
 */
export interface Cable {
	id: string;
	from: string;
	to: string;
	points: Pt[];
	material?: string;
	groupId?: string;
}

export type ElectricalGroupPurpose =
	| "lighting"
	| "outlets"
	| "dedicated"
	| "network"
	| "tv"
	| "other";

export interface ElectricalGroup {
	id: string;
	name: string;
	purpose: ElectricalGroupPurpose;
	panelId?: string;
}

/** Визуальная связь светильника с настенным электроприбором на плане. */
export interface LightingConnection {
	id: string;
	from: string;
	to: string;
	style: "arc" | "line";
	key?: 1 | 2 | 3;
}

/** К чему привязан комментарий: предмет на полу, светильник, прибор на стене, проём, стена. */
export type CommentTargetKind = "item" | "light" | "fixture" | "opening" | "wall";

/**
 * Привязка комментария к объекту. `offset` — где на объекте кончается стрелка:
 * у предмета и светильника — в осях самого предмета (повернули — точка повернулась
 * с ним), у прибора и проёма — от их точки на плане, у стены — вдоль неё от узла `a`
 * (`x`) и поперёк неё (`y`).
 */
export interface CommentTarget {
	kind: CommentTargetKind;
	id: string;
	offset: Pt;
}

/**
 * Комментарий на чертеже — выноска, как в Ремпланнере: текст в рамке и стрелка.
 * Свободный комментарий указывает на точку плана (`anchor`). Привязанный (`target`)
 * указывает на объект: сдвинули диван — стрелка поехала за ним, а `anchor` хранит, куда
 * она указывала в последний раз (на случай, если объекта не станет). Привязанный
 * комментарий принадлежит слою своего объекта: слой выключили — комментарий не виден.
 * Рамка (`box` — её центр) двигается отдельно и к объекту не привязана; стоит рамка
 * на самой точке — стрелки нет, это просто надпись.
 * Кегль `fontSize` — в экранных пикселях, как у подписей комнат: при зуме не меняется.
 */
export interface PlanComment {
	id: string;
	anchor: Pt;
	box: Pt;
	text: string;
	fontSize: number;
	/** Цвет текста, рамки и стрелки */
	color: string;
	/** Объект, на который указывает стрелка; нет — комментарий свободный */
	target?: CommentTarget;
}

/**
 * Труба водоснабжения по полу между двумя точками слоя сантехники: стояком,
 * коллектором или водорозеткой (`fixtures`). Положение концов не хранится, а
 * считается из стены; `points` — повороты трассы. `medium` — холодная или горячая
 * вода: от неё цвет на плане и к каким точкам трубу можно подвести.
 * `material` — id материала из раздела «Труба»; нет — труба по умолчанию.
 */
export interface Pipe {
	id: string;
	from: string;
	to: string;
	points: Pt[];
	medium: WaterMedium;
	material?: string;
}

/**
 * Отделка одной стороны стены: ссылка на элемент раздела «Отделка стен» и цвет,
 * если пользователь его поменял. Сторона — как у `Fixture.side`: 1 — по левой нормали
 * направления a → b. Ключ в слое — `wallFaceKey(wallId, side)`.
 */
export interface WallFinish {
	id: string;
	wallId: string;
	side: 1 | -1;
	/** Id элемента каталога отделки */
	finish: string;
	color?: string;
}

/** Сторона стены — то, чему назначается отделка. */
export interface WallFace {
	wallId: string;
	side: 1 | -1;
}

/** Ключ стороны стены в слое отделки. */
export const wallFaceKey = (wallId: string, side: 1 | -1): string => `${wallId}:${side}`;

export { LayerKind } from "./layerKind";

export interface LayerBase<K extends LayerKind> {
	kind: K;
	visible: boolean;
}

export interface WallGraph {
	nodes: Record<string, PlanNode>;
	walls: Record<string, Wall>;
	openings: Record<string, Opening>;
}

export interface WallsLayer extends LayerBase<LayerKind.Walls>, WallGraph {}

export interface ItemsLayer<K extends LayerKind = LayerKind> extends LayerBase<K> {
	fixtures: Record<string, Fixture>;
	items: Record<string, FloorItem>;
}

/**
 * Своё название комнаты («Кухня» вместо «ПО 3»). Хранится по id комнаты, как покрытия:
 * сама комната вычисляется из стен, а название — то, что пользователь ей дал.
 */
export interface RoomName {
	id: string;
	roomId: string;
	name: string;
}

/** Комнаты вычисляются из стен; слой хранит только их названия. */
export interface RoomsLayer extends LayerBase<LayerKind.Rooms> {
	items: Record<string, RoomName>;
}

export interface FloorsLayer extends LayerBase<LayerKind.Floors> {
	items: Record<string, RoomFloor>;
}

export interface WaterproofLayer extends LayerBase<LayerKind.Waterproof> {
	items: Record<string, RoomWaterproof>;
}

export interface ScreedLayer extends LayerBase<LayerKind.Screed> {
	items: Record<string, RoomScreed>;
}

export interface CeilingLayer extends LayerBase<LayerKind.Ceiling> {
	items: Record<string, RoomCeiling>;
}

export interface BaseboardLayer extends LayerBase<LayerKind.Baseboard> {
	items: Record<string, RoomBaseboard>;
}

export interface WallFinishLayer extends LayerBase<LayerKind.WallFinish> {
	items: Record<string, WallFinish>;
}

export interface WiringLayer extends LayerBase<LayerKind.Wiring> {
	items: Record<string, Cable>;
	connections: Record<string, LightingConnection>;
	groups: Record<string, ElectricalGroup>;
}

export interface PipesLayer extends LayerBase<LayerKind.Pipes> {
	items: Record<string, Pipe>;
}

export interface CommentsLayer extends LayerBase<LayerKind.Comments> {
	items: Record<string, PlanComment>;
}

/** Картинка подложки: габариты в проекте и ссылка на локальное хранилище картинок браузера. */
export interface UnderlayImage {
	/** Ключ картинки в отдельном браузерном JSON-хранилище (`grid-planner:underlays`) */
	id?: string;
	/** Картинка (data URL) или ссылка `underlay:<id>` при сериализации проекта */
	src: string;
	/** Размер картинки, пикселей */
	width: number;
	height: number;
}

/**
 * Подложка — фото или скан плана квартиры (план БТИ) под чертежом: по нему обводят стены.
 * Это не часть чертежа: в 3D, на развёртках и на листах PDF её нет.
 */
export interface UnderlayLayer extends LayerBase<LayerKind.Underlay> {
	/** null — подложку не загружали */
	image: UnderlayImage | null;
	/** Левый верхний угол картинки на плане, мм */
	x: number;
	y: number;
	/** Сколько миллиметров плана в одном пикселе картинки — масштаб подложки */
	mmPerPixel: number;
	/** Непрозрачность, 0…1: подложку приглушают, чтобы стены поверх читались */
	opacity: number;
}

export interface PlanLayers {
	floors: FloorsLayer;
	waterproof: WaterproofLayer;
	screed: ScreedLayer;
	rooms: RoomsLayer;
	walls: WallsLayer;
	ceiling: CeilingLayer;
	baseboard: BaseboardLayer;
	electrical: ItemsLayer<LayerKind.Electrical>;
	plumbing: ItemsLayer<LayerKind.Plumbing>;
	furniture: ItemsLayer<LayerKind.Furniture>;
	wiring: WiringLayer;
	pipes: PipesLayer;
	finishes: WallFinishLayer;
	comments: CommentsLayer;
	underlay: UnderlayLayer;
}

export const getPlanLayer = (planLayers: PlanLayers, kind: LayerKind) => {
	switch (kind) {
		case LayerKind.Floors:
			return planLayers.floors;
		case LayerKind.Waterproof:
			return planLayers.waterproof;
		case LayerKind.Rooms:
			return planLayers.rooms;
		case LayerKind.Walls:
			return planLayers.walls;
		case LayerKind.Ceiling:
			return planLayers.ceiling;
		case LayerKind.Baseboard:
			return planLayers.baseboard;
		case LayerKind.Wiring:
			return planLayers.wiring;
		case LayerKind.Electrical:
			return planLayers.electrical;
		case LayerKind.Plumbing:
			return planLayers.plumbing;
		case LayerKind.Furniture:
			return planLayers.furniture;
		case LayerKind.Screed:
			return planLayers.screed;
		case LayerKind.Pipes:
			return planLayers.pipes;
		case LayerKind.WallFinish:
			return planLayers.finishes;
		case LayerKind.Comments:
			return planLayers.comments;
		case LayerKind.Underlay:
			return planLayers.underlay;
		default:
			throw new Error(`Unknown layer kind: ${kind}`);
	}
};
